import React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const STATUS_COLORS = {
  "Passed": "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  "Signed": "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  "Passed Committee": "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  "In Committee": "bg-brand-gold-50 text-[#9A7212] border-brand-gold/40",
  "On Calendar": "bg-brand-civic-50 text-brand-civic border-brand-civic/30",
  "Filed": "bg-slate-100 text-slate-600 border-slate-300",
  "Failed": "bg-red-50 text-red-600 border-red-200",
  "Vetoed": "bg-red-50 text-red-600 border-red-200",
};

export function StatusBadge({ status, className }) {
  return (
    <span data-testid="status-badge" className={cn(
      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
      STATUS_COLORS[status] || "bg-slate-100 text-slate-600 border-slate-300", className)}>
      {status}
    </span>
  );
}

const PRIORITY = {
  high: "bg-red-50 text-red-600 border-red-200",
  medium: "bg-brand-gold-50 text-[#9A7212] border-brand-gold/40",
  low: "bg-slate-100 text-slate-500 border-slate-300",
};
export function PriorityBadge({ priority, className }) {
  return (
    <span data-testid="priority-badge" className={cn(
      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize",
      PRIORITY[priority] || PRIORITY.low, className)}>
      {priority} priority
    </span>
  );
}

export function SourceBadge({ type }) {
  const isSample = type === "sample";
  return (
    <span data-testid="source-badge" className={cn(
      "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
      isSample ? "bg-brand-civic-50 text-brand-civic border-brand-civic/40" : "bg-brand-teal-50 text-brand-teal border-brand-teal/40")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", isSample ? "bg-brand-civic" : "bg-brand-teal")} />
      {isSample ? "Sample" : "Live"}
    </span>
  );
}

export function ConnectorBadge({ enabled }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
      enabled ? "bg-brand-teal-50 text-brand-teal border-brand-teal/40" : "bg-slate-100 text-slate-500 border-slate-300")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", enabled ? "bg-brand-teal" : "bg-slate-400")} />
      {enabled ? "Live" : "Not Enabled"}
    </span>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-500 gap-2" data-testid="loading">
      <Loader2 className="h-5 w-5 animate-spin text-brand-civic" /> {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed border-brand-line bg-white/60" data-testid="empty-state">
      {Icon && (
        <div className="h-12 w-12 rounded-full bg-brand-civic-50 flex items-center justify-center mb-3">
          <Icon className="h-6 w-6 text-brand-civic" />
        </div>
      )}
      <p className="text-brand-ink font-semibold">{title}</p>
      {description && <p className="text-slate-500 text-sm mt-1 max-w-sm">{description}</p>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink">{title}</h1>
        {subtitle && <p className="text-slate-500 text-sm mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn("rounded-xl border border-brand-line bg-white p-5 shadow-card", className)} {...props}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "text-brand-civic", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-brand-line bg-white p-4 shadow-card hover:shadow-elevated hover:border-brand-civic/40 transition-all">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && (
          <span className="h-7 w-7 rounded-lg bg-brand-civic-50 flex items-center justify-center">
            <Icon className={cn("h-4 w-4", accent)} />
          </span>
        )}
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-brand-navy">{value}</div>
    </div>
  );
}
